// src/routes/MasterDataRoutes.jsx
/**
 * Master Data Routes
 *
 * Nested router untuk semua module Master Data.
 * Handles all routes under /masterdata/*
 *
 * Mengikuti pola UserManagementRoutes:
 * - Setiap route dilindungi HasPermission
 * - index route → List component
 * - create route → Form component (mode add)
 * - :id/edit route → Form component (mode edit)
 * - :id route → Detail component (view only) — uncomment saat component siap
 *
 * Sub-modules (sesuai menu Master Data):
 *
 *   /masterdata/customers            → Customer management
 *   /masterdata/spk                  → SPK management
 *   /masterdata/projects             → Project management
 *   /masterdata/fo-segments          → FO Segment management
 *   /masterdata/tower-sites          → Tower Site management
 *   /masterdata/items                → Item management
 *   /masterdata/main-areas           → Main Area & Coefficient management
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// Auth
import { HasPermission } from '../components/auth';
import { PERMISSIONS } from '../components/auth/constants';

// ============================================================================
// CUSTOMER
// ============================================================================
import CustomerList from '../pages/masterdata/customer/CustomerList';
import CustomerForm from '../pages/masterdata/customer/CustomerForm';
// import CustomerDetail from '../pages/masterdata/customers/CustomerDetail';

// ============================================================================
// SPK
// ============================================================================
import SPKList from '../pages/masterdata/spk/SPKList';
import SPKForm from '../pages/masterdata/spk/SPKForm';
// import SPKDetail from '../pages/masterdata/spk/SPKDetail';

// ============================================================================
// PROJECT
// ============================================================================
import ProjectList from '../pages/masterdata/project/ProjectList';
import ProjectForm from '../pages/masterdata/project/ProjectForm';
// import ProjectDetail from '../pages/masterdata/projects/ProjectDetail';

// ============================================================================
// FO SEGMENT
// ============================================================================
import FOSegmentList from '../pages/masterdata/fosegment/FOSegmentList';
import FOSegmentForm from '../pages/masterdata/fosegment/FOSegmentForm';
// import FOSegmentDetail from '../pages/masterdata/fo-segments/FOSegmentDetail';

// ============================================================================
// TOWER SITE — uncomment saat TowerSiteList/Form sudah dibuat
// ============================================================================
import TowerSiteList from '../pages/masterdata/towersite/TowerSiteList';
import TowerSiteForm from '../pages/masterdata/towersite/TowerSiteForm';
// import TowerSiteDetail from '../pages/masterdata/tower-sites/TowerSiteDetail';

// ============================================================================
// ITEM
// ============================================================================
import ItemList from '../pages/masterdata/item/ItemList';
import ItemForm from '../pages/masterdata/item/ItemForm';
// import ItemDetail from '../pages/masterdata/items/ItemDetail';

// ============================================================================
// MAIN AREA & COEFFICIENT
// ============================================================================
import MainAreaList from '../pages/masterdata/mainarea/MainAreaList';
import MainAreaForm from '../pages/masterdata/mainarea/MainAreaForm';
// import MainAreaDetail from '../pages/masterdata/main-areas/MainAreaDetail';

// ============================================================================
// MAIN ROUTER
// ============================================================================
const MasterDataRoutes = () => {
  return (
    <Routes>

      {/* ================================================================== */}
      {/* DEFAULT REDIRECT → Customer (menu pertama)                         */}
      {/* ================================================================== */}
      <Route path="/" element={<Navigate to="/masterdata/customers" replace />} />


      {/* ================================================================== */}
      {/* CUSTOMER MODULE                                                     */}
      {/* ================================================================== */}

      {/*
        INDEX - List Customers
        Path: /masterdata/customers
        Permission: CUSTOMER_VIEW
      */}
      <Route
        path="customers"
        element={
          <HasPermission permission={PERMISSIONS.CUSTOMER_VIEW}>
            <CustomerList />
          </HasPermission>
        }
      />

      {/*
        CREATE - Add New Customer
        Path: /masterdata/customers/create
        Permission: CUSTOMER_CREATE
      */}
      <Route
        path="customers/create"
        element={
          <HasPermission permission={PERMISSIONS.CUSTOMER_CREATE}>
            <CustomerForm />
          </HasPermission>
        }
      />

      {/*
        EDIT - Edit Existing Customer
        Path: /masterdata/customers/:id/edit
        Permission: CUSTOMER_UPDATE
      */}
      <Route
        path="customers/:id/edit"
        element={
          <HasPermission permission={PERMISSIONS.CUSTOMER_UPDATE}>
            <CustomerForm />
          </HasPermission>
        }
      />

      {/*
        DETAIL - View Customer Detail
        Path: /masterdata/customers/:id
        Permission: CUSTOMER_VIEW
        Uncomment jika CustomerDetail component sudah dibuat
      */}
      {/*
      <Route
        path="customers/:id"
        element={
          <HasPermission permission={PERMISSIONS.CUSTOMER_VIEW}>
            <CustomerDetail />
          </HasPermission>
        }
      />
      */}


      {/* ================================================================== */}
      {/* SPK MODULE                                                          */}
      {/* ================================================================== */}

      {/*
        INDEX - List SPKs
        Path: /masterdata/spk
        Permission: SPK_VIEW
      */}
      <Route
        path="spk"
        element={
          <HasPermission permission={PERMISSIONS.SPK_VIEW}>
            <SPKList />
          </HasPermission>
        }
      />

      {/*
        CREATE - Add New SPK
        Path: /masterdata/spk/create
        Permission: SPK_CREATE
      */}
      <Route
        path="spk/create"
        element={
          <HasPermission permission={PERMISSIONS.SPK_CREATE}>
            <SPKForm />
          </HasPermission>
        }
      />

      {/*
        EDIT - Edit Existing SPK
        Path: /masterdata/spk/:id/edit
        Permission: SPK_UPDATE
      */}
      <Route
        path="spk/:id/edit"
        element={
          <HasPermission permission={PERMISSIONS.SPK_UPDATE}>
            <SPKForm />
          </HasPermission>
        }
      />

      {/*
        DETAIL - View SPK Detail
        Path: /masterdata/spk/:id
        Permission: SPK_VIEW
        Uncomment jika SPKDetail sudah diextract sebagai standalone component
      */}
      {/*
      <Route
        path="spk/:id"
        element={
          <HasPermission permission={PERMISSIONS.SPK_VIEW}>
            <SPKDetail />
          </HasPermission>
        }
      />
      */}


      {/* ================================================================== */}
      {/* PROJECT MODULE                                                      */}
      {/* ================================================================== */}

      {/*
        INDEX - List Projects
        Path: /masterdata/projects
        Permission: PROJECT_VIEW
      */}
      <Route
        path="projects"
        element={
          <HasPermission permission={PERMISSIONS.PROJECT_VIEW}>
            <ProjectList />
          </HasPermission>
        }
      />

      {/*
        CREATE - Add New Project
        Path: /masterdata/projects/create
        Permission: PROJECT_CREATE
      */}
      <Route
        path="projects/create"
        element={
          <HasPermission permission={PERMISSIONS.PROJECT_CREATE}>
            <ProjectForm />
          </HasPermission>
        }
      />

      {/*
        EDIT - Edit Existing Project
        Path: /masterdata/projects/:id/edit
        Permission: PROJECT_UPDATE
      */}
      <Route
        path="projects/:id/edit"
        element={
          <HasPermission permission={PERMISSIONS.PROJECT_UPDATE}>
            <ProjectForm />
          </HasPermission>
        }
      />

      {/*
        DETAIL - View Project Detail
        Path: /masterdata/projects/:id
        Permission: PROJECT_VIEW
        Uncomment jika ProjectDetail sudah dibuat
      */}
      {/*
      <Route
        path="projects/:id"
        element={
          <HasPermission permission={PERMISSIONS.PROJECT_VIEW}>
            <ProjectDetail />
          </HasPermission>
        }
      />
      */}


      {/* ================================================================== */}
      {/* FO SEGMENT MODULE                                                   */}
      {/* ================================================================== */}

      {/*
        INDEX - List FO Segments
        Path: /masterdata/fosegments
        Permission: FO_SEGMENT_VIEW
      */}
      <Route
        path="fosegments"
        element={
          <HasPermission permission={PERMISSIONS.FO_SEGMENT_VIEW}>
            <FOSegmentList />
          </HasPermission>
        }
      />

      {/*
        CREATE - Add New FO Segment
        Path: /masterdata/fo-segments/create
        Permission: FO_SEGMENT_CREATE
      */}
      <Route
        path="fosegments/create"
        element={
          <HasPermission permission={PERMISSIONS.FO_SEGMENT_CREATE}>
            <FOSegmentForm />
          </HasPermission>
        }
      />

      {/*
        EDIT - Edit Existing FO Segment
        Path: /masterdata/fo-segments/:id/edit
        Permission: FO_SEGMENT_UPDATE
      */}
      <Route
        path="fosegments/:id/edit"
        element={
          <HasPermission permission={PERMISSIONS.FO_SEGMENT_UPDATE}>
            <FOSegmentForm />
          </HasPermission>
        }
      />

      {/*
        DETAIL - View FO Segment Detail
        Path: /masterdata/fo-segments/:id
        Permission: FO_SEGMENT_VIEW
        Uncomment jika FOSegmentDetail sudah dibuat
      */}
      {/*
      <Route
        path="fo-segments/:id"
        element={
          <HasPermission permission={PERMISSIONS.FO_SEGMENT_VIEW}>
            <FOSegmentDetail />
          </HasPermission>
        }
      />
      */}


      {/* ================================================================== */}
      {/* TOWER SITE MODULE                                                   */}
      {/* Uncomment seluruh blok ini ketika TowerSite components sudah siap  */}
      {/* ================================================================== */}

      {/* */}
      <Route
        path="towersites"
        element={
          <HasPermission permission={PERMISSIONS.TOWER_SITE_VIEW}>
            <TowerSiteList />
          </HasPermission>
        }
      />
      <Route
        path="towersites/create"
        element={
          <HasPermission permission={PERMISSIONS.TOWER_SITE_CREATE}>
            <TowerSiteForm />
          </HasPermission>
        }
      />
      <Route
        path="towersites/:id/edit"
        element={
          <HasPermission permission={PERMISSIONS.TOWER_SITE_UPDATE}>
            <TowerSiteForm />
          </HasPermission>
        }
      />
      {/* 
      <Route
        path="towersites/:id"
        element={
          <HasPermission permission={PERMISSIONS.TOWER_SITE_VIEW}>
            <TowerSiteDetail />
          </HasPermission>
        }
      />
      */}


      {/* ================================================================== */}
      {/* ITEM MODULE                                                         */}
      {/* ================================================================== */}

      {/*
        INDEX - List Items
        Path: /masterdata/items
        Permission: ITEM_VIEW
      */}
      <Route
        path="items"
        element={
          <HasPermission permission={PERMISSIONS.ITEM_VIEW}>
            <ItemList />
          </HasPermission>
        }
      />

      {/*
        CREATE - Add New Item
        Path: /masterdata/items/create
        Permission: ITEM_CREATE
      */}
      <Route
        path="items/create"
        element={
          <HasPermission permission={PERMISSIONS.ITEM_CREATE}>
            <ItemForm />
          </HasPermission>
        }
      />

      {/*
        EDIT - Edit Existing Item
        Path: /masterdata/items/:id/edit
        Permission: ITEM_UPDATE
      */}
      <Route
        path="items/:id/edit"
        element={
          <HasPermission permission={PERMISSIONS.ITEM_UPDATE}>
            <ItemForm />
          </HasPermission>
        }
      />

      {/*
        DETAIL - View Item Detail
        Path: /masterdata/items/:id
        Permission: ITEM_VIEW
        Uncomment jika ItemDetail sudah dibuat
      */}
      {/*
      <Route
        path="items/:id"
        element={
          <HasPermission permission={PERMISSIONS.ITEM_VIEW}>
            <ItemDetail />
          </HasPermission>
        }
      />
      */}


      {/* ================================================================== */}
      {/* MAIN AREA & COEFFICIENT MODULE                                      */}
      {/* ================================================================== */}

      {/*
        INDEX - List Main Areas
        Path: /masterdata/main-areas
        Permission: MAIN_AREA_VIEW
      */}
      <Route
        path="mainareas"
        element={
          <HasPermission permission={PERMISSIONS.MAIN_AREA_VIEW}>
            <MainAreaList />
          </HasPermission>
        }
      />

      {/*
        CREATE - Add New Main Area
        Path: /masterdata/main-areas/create
        Permission: MAIN_AREA_CREATE
      */}
      <Route
        path="mainareas/create"
        element={
          <HasPermission permission={PERMISSIONS.MAIN_AREA_CREATE}>
            <MainAreaForm />
          </HasPermission>
        }
      />

      {/*
        EDIT - Edit Existing Main Area
        Path: /masterdata/main-areas/:id/edit
        Permission: MAIN_AREA_UPDATE
      */}
      <Route
        path="mainareas/:id/edit"
        element={
          <HasPermission permission={PERMISSIONS.MAIN_AREA_UPDATE}>
            <MainAreaForm />
          </HasPermission>
        }
      />

      {/*
        DETAIL - View Main Area Detail
        Path: /masterdata/main-areas/:id
        Permission: MAIN_AREA_VIEW
        Uncomment jika MainAreaDetail sudah dibuat
      */}
      {/*
      <Route
        path="main-areas/:id"
        element={
          <HasPermission permission={PERMISSIONS.MAIN_AREA_VIEW}>
            <MainAreaDetail />
          </HasPermission>
        }
      />
      */}


      {/* ================================================================== */}
      {/* FALLBACK                                                            */}
      {/* ================================================================== */}
      <Route path="*" element={<Navigate to="/masterdata/customers" replace />} />

    </Routes>
  );
};

export default MasterDataRoutes;